import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import FormDialog from "components/modal";
import { baseURL, imageURL } from "services/API";
import {Box,Button,Typography,Grid,styled,Avatar,Stack} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LandlordTenantPaymentsTable from "components/Tables/LandlordTenantPaymentsTable";
import LandlordTenantPaymentsForm from "components/Forms/LandlordTenantPaymentsForm"
import {  useGetTenantOccupancyActivePeriodsData, useTenantPayments } from "services/hooks/rentals";
import { setRefreshCount } from "state";
import { useSelector, useDispatch } from "react-redux";
import BasicCard from "components/Card";
import FlexBetween from "components/FlexBetween";
import { currencyFormatter, withoutSymbolFormatter } from "utilities/currencyFormatter";
import moment from "moment";
import * as yup from 'yup';
import EditTenantPaymentForm from "components/Forms/EditLandlordTenantPaymentsForm";
import TopSnack from "components/TopSnack";
import DummyTable from "components/Tables/DummyTable";
import { parseApprovalResponse, apiErrorMessage } from "utilities/approvalResponse";
import TenantAgreementActions from "components/TenantAgreementActions";
import TenantUtilitiesStatus from "components/TenantUtilitiesStatus";

const findLatestSuccessfulPaymentId = (payments) => {
  // API returns successful payments with latest first; soft-deleted (voided) have status=false.
  const successful = (payments || []).filter((p) => p.status === true);
  if (!successful.length) return null;
  return successful[0].id;
};

// Style definition for the title container
const TitleContainer = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const LandlordTenantsPayments = () => {
  const [page, setPage] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newPayment, setNewPayment] = useState({
    related_rental_unit: "",
    related_tenant: "",
    amount: undefined,
    payment_method: "",
    date_paid: moment().format('YYYY-MM-DD'),
  });

  const dispatch = useDispatch()

  const [amount, setAmount] = useState(newPayment.amount)
  const [paymentMethod, setPaymentMethod] = useState(newPayment.payment_method)
  const [datePaid, setDatePaid] = useState(newPayment.date_paid)
  const [initialPayment, setInitialPayment] = React.useState(false);
  const [hasSecurityDeposit, setHasSecurityDeposit] = React.useState(false);
  const [nextPaymentDate, setNextPaymentDate] = useState("")
  const [errors, setErrors] = useState({})

  const [isSubmittingData, setIsSubmittingData] = useState(false);

  const landlord = useSelector((state) => state.landlord);
  const tenantOccupancy = useSelector((state) => state.tenantOccupancy);
  const {tenantPayments, isLoadingTenantPayments, errorTenantPayments} = useTenantPayments(tenantOccupancy.id)
  const {activePeriods, isLoadingActivePeriods, errorLoadingActivePeriods} = useGetTenantOccupancyActivePeriodsData(tenantOccupancy.id)

  const tenant = tenantOccupancy?.related_tenant;
  const unit = tenantOccupancy?.related_rental_unit;
  const property = unit?.related_rental;
  const [message, setMessage] = React.useState("");
  const [openSnack, setOpenSnack] = React.useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const [severity, setSeverity] = React.useState("");
  const [voidablePaymentId, setVoidablePaymentId] = useState(null);
  const [paymentToVoid, setPaymentToVoid] = useState(null);
  const [openVoidDialog, setOpenVoidDialog] = useState(false);
  const [isVoidingPayment, setIsVoidingPayment] = useState(false);

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack({ ...openSnack, open: false });
  };

  const refreshVoidablePayment = useCallback(async () => {
    const schedules = activePeriods?.schedules;
    if (!Array.isArray(schedules) || schedules.length === 0) {
      setVoidablePaymentId(null);
      return;
    }
    try {
      // Periods are returned latest-first; scan in order and take the first successful payment.
      for (const schedule of schedules) {
        const periodId = schedule?.related_active_period?.id;
        if (!periodId) continue;
        const response = await axios.get(
          `${baseURL}/tenants/completed_payments/payments_list?period_id=${periodId}`
        );
        const payments = Array.isArray(response.data?.data) ? response.data.data : [];
        const latestId = findLatestSuccessfulPaymentId(payments);
        if (latestId) {
          setVoidablePaymentId(latestId);
          return;
        }
      }
      setVoidablePaymentId(null);
    } catch {
      setVoidablePaymentId(null);
    }
  }, [activePeriods?.schedules]);

  useEffect(() => {
    refreshVoidablePayment();
  }, [refreshVoidablePayment]);


	const handleChangeAmount = (event) => {
    const {value} = event.target
		setAmount(value);
    if (!value.trim()) {
      setErrors(prev=> ({...prev , 'amount': 'This is required'}))
    } else {
      setErrors((prev) => ({ ...prev, amount: "" }));
    }
	};

	const handleChangeDatePaid = (event) => {
    const {value} = event.target
		setDatePaid(value);
	};

  const handleChangeNextPaymentDate = (event) => {
    const {value} = event.target
		setNextPaymentDate(value);
	};

	const handleChangePaymentMethod = (event) => {
    const {value} = event.target
		setPaymentMethod(value);
    if (!toString(value).trim()) {
      setErrors((prev) => ({ ...prev, payment_method: "This is required" }));
    } else {
      setErrors((prev) => ({ ...prev, payment_method: "" }));
    }
	};

	const handleInitialPayment = (event) => {
		setInitialPayment(event.target.checked);
	};

  const handleSecurityDeposit = (event) => {
		setHasSecurityDeposit(event.target.checked);
	};

  const rowsPerPage = 10;

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewPayment({
      related_rental_unit: "",
      related_tenant: "",
      amount: undefined,
      payment_method: "",
      date_paid: "",
    });
    setInitialPayment(false);
    setHasSecurityDeposit(false);

  };

  const handleOpenEditDialog = (payment) => {
    setNewPayment(payment)
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setNewPayment({
      related_rental_unit: "",
      related_tenant: "",
      amount: undefined,
      payment_method: "",
      date_paid: "",
    });
    setInitialPayment(false);
    setHasSecurityDeposit(false);
  };

  const handleOpenDeleteDialog = (payment) => {
    setNewPayment(payment)
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setNewPayment({
      related_rental_unit: "",
      related_tenant: "",
      amount: undefined,
      payment_method: "",
      date_paid: "",
    });
    setInitialPayment(false);
    setHasSecurityDeposit(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewPayment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const paymentSchema = yup.object().shape({
    related_rental_unit: yup.number().required('This is required'),
    related_tenant: yup.number().required('This is required'),
    amount: yup.number().required('This is required'),
    payment_method: yup.string().required('This is required'),
    date_paid: yup.date().required('This is required'),
  });

  const handleSubmitTenantPayment = async () => {
    try {
			await paymentSchema.validate(
        {
          related_rental_unit: tenantOccupancy.related_rental_unit.id,
          related_tenant: tenantOccupancy.related_tenant.id,
          amount: amount,
          date_paid: datePaid,
          payment_method: paymentMethod,
          is_initial_payment: initialPayment,
          has_security_deposit: hasSecurityDeposit,
          next_payment_date: nextPaymentDate,
        }, {abortEarly:false});
		} catch (error) {
			const newError = {}
			error.inner.forEach(err => {
				newError[err.path]=err.message
			})
			setErrors(newError)
			return
		}
		try {
      const paymentPayload = {
        related_rental_unit: tenantOccupancy.related_rental_unit.id,
        related_tenant: tenantOccupancy.related_tenant.id,
        amount: amount,
        date_paid: datePaid,
        payment_method: paymentMethod,
        is_initial_payment: initialPayment,
        has_security_deposit: hasSecurityDeposit,
        next_payment_date: nextPaymentDate,
      };
      const response = await axios.post(`${baseURL}/tenants/payments/custom`, paymentPayload);
      const approval = parseApprovalResponse(response);
      dispatch(setRefreshCount());
			setOpenDialog(false);
			setOpenSnack({ ...openSnack, open: true});
			if (approval.pendingApproval) {
        setSeverity("info");
        setMessage(
          approval.message ||
          `Submitted for approval (request #${approval.approvalRequestId}). Check Audit & Approvals to complete after sign-off.`
        );
      } else {
        setSeverity("success");
        setMessage("Payment recorded successfully");
      }
		} catch (err) {
			setOpenSnack({ ...openSnack, open: true});
			setSeverity("error");
			setMessage(apiErrorMessage(err, "Payment failed. Please check the data provided."));
		}
	};

  const handleSubmitEdittedTenantPayment = async () => {
    try {
      await paymentSchema.validate(
        {
          related_rental_unit: tenantOccupancy.related_rental_unit.id,
          related_tenant: tenantOccupancy.related_tenant.id,
          amount: newPayment.amount,
          date_paid: newPayment.date_paid,
          payment_method: typeof newPayment.payment_method === 'object'
            ? newPayment.payment_method?.id
            : newPayment.payment_method,
          is_initial_payment: newPayment.is_initial_payment,
          has_security_deposit: newPayment.has_security_deposit,
          next_payment_date: nextPaymentDate,
        },
        { abortEarly: false }
      );
    } catch (error) {
      const newError = {};
      error.inner.forEach((err) => {
        newError[err.path] = err.message;
      });
      setErrors(newError);
      return;
    }
    try {
      const paymentPayload = {
        related_rental_unit: tenantOccupancy.related_rental_unit.id,
        related_tenant: tenantOccupancy.related_tenant.id,
        amount: newPayment.amount,
        date_paid: newPayment.date_paid,
        payment_method: typeof newPayment.payment_method === 'object'
          ? newPayment.payment_method?.id
          : newPayment.payment_method,
        is_initial_payment: newPayment.is_initial_payment,
        has_security_deposit: newPayment.has_security_deposit,
        next_payment_date: nextPaymentDate,
      };
      const response = await axios.post(`${baseURL}/tenants/payments/custom`, paymentPayload);
      const approval = parseApprovalResponse(response);
      dispatch(setRefreshCount());
      setOpenEditDialog(false);
      setOpenSnack({ ...openSnack, open: true});
      if (approval.pendingApproval) {
        setSeverity("info");
        setMessage(
          approval.message ||
          `Submitted for approval (request #${approval.approvalRequestId}).`
        );
      } else {
        setSeverity("success");
        setMessage("Payment updated successfully");
      }
    } catch (err) {
      setOpenSnack({ ...openSnack, open: true});
      setSeverity("error");
      setMessage(apiErrorMessage(err, "Payment update failed."));
    }
  };

  const handleSubmitDeleteTenantPayment = async () => {
    
    try {
      // const response = await axios.post(`${baseURL}/tenants/payments/custom`);
      // dispatch(setRefreshCount());
      // setOpenDeleteDialog(false);
      // setOpenSnack({ ...openSnack, open: true});
      // setSeverity("success");
      // setMessage("Tenant Added Successfully");
    } catch (err) {
      // setOpenSnack({ ...openSnack, open: true});
      // setSeverity("error");
      // setMessage("Tenant Addition failed, Please check data provided");
    }
  };

  const handleOpenVoidDialog = (payment) => {
    setPaymentToVoid(payment);
    setOpenVoidDialog(true);
  };

  const handleCloseVoidDialog = () => {
    if (isVoidingPayment) return;
    setOpenVoidDialog(false);
    setPaymentToVoid(null);
  };

  const handleSubmitVoidPayment = async () => {
    if (!paymentToVoid?.id) return;
    setIsVoidingPayment(true);
    try {
      const response = await axios.post(`${baseURL}/tenants/payments/${paymentToVoid.id}/void`, {});
      const approval = parseApprovalResponse(response);
      setOpenVoidDialog(false);
      setPaymentToVoid(null);
      setOpenSnack({ ...openSnack, open: true });
      if (approval.pendingApproval) {
        setSeverity("info");
        setMessage(
          approval.message ||
            `Void submitted for approval (request #${approval.approvalRequestId}). Check Audit & Approvals to complete after sign-off.`
        );
      } else {
        setSeverity("success");
        setMessage("Payment voided successfully.");
        dispatch(setRefreshCount());
        await refreshVoidablePayment();
      }
    } catch (err) {
      setOpenSnack({ ...openSnack, open: true });
      setSeverity("error");
      setMessage(apiErrorMessage(err, "Could not void payment. Only the latest successful payment can be voided."));
    } finally {
      setIsVoidingPayment(false);
    }
  };
  

  return (
    <Box m="20px">
      <Box sx={{ mb: 3, pl: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar 
              src={tenant?.profile_picture ? `${imageURL}${tenant.profile_picture}` : undefined}
              sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 600
              }}
            >
              {!tenant?.profile_picture && 
                (tenant?.first_name?.charAt(0) || 'T').toUpperCase()
              }
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {tenant?.first_name} {tenant?.last_name}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                {tenant?.tenant_number && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Tenant #{tenant.tenant_number}
                    </Typography>
                  </Stack>
                )}
                {tenant?.phone_number && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {tenant.phone_number}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                Unit: <strong>{unit?.unit_name}</strong> - {property?.rental_name}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1.5} alignItems="flex-end">
            <TenantAgreementActions
              tenantId={tenant?.id}
              occupancyId={tenantOccupancy?.id}
              tenantFirstName={tenant?.first_name}
              tenantLastName={tenant?.last_name}
              unitName={unit?.unit_name}
            />
            <TenantUtilitiesStatus
              tenantId={tenant?.id}
              occupancyId={tenantOccupancy?.id}
            />
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ p: 3 }}>
        <FlexBetween>
          <BasicCard name="Days Left" value={activePeriods.days_left} />
          <BasicCard name="Unit Rent" value={activePeriods.unit_rent} />
          <BasicCard name="Current Outstanding Balance" value={currencyFormatter(parseFloat(activePeriods.total_outstanding_balance))}/>
          <BasicCard name="Total Complete Months" value={activePeriods.completed_months} />
          <Button variant="contained" color="primary" sx={{ height: 56, width: 200 }} onClick={handleOpenDialog}>Record Payment</Button>
        </FlexBetween>

        <Box sx={{ mt: 5 }}>
          {isLoadingActivePeriods ? (
            <DummyTable/>
          ):(
            <LandlordTenantPaymentsTable
              page={page}
              rowsPerPage={rowsPerPage}
              handleChangePage={handleChangePage}
              paymentData={activePeriods.schedules}
              handleDeletePayment={handleOpenDeleteDialog}
              handleEditPayment={handleOpenEditDialog}
              voidablePaymentId={voidablePaymentId}
              onVoidPayment={handleOpenVoidDialog}
              isVoidingPayment={isVoidingPayment}
            />
          )}
        </Box>
      </Box>

      <FormDialog
        open={openDialog}
        title="Add Payment"
        text="Fill out the details below to add a new payment."
        content={
          <LandlordTenantPaymentsForm
            handleChangeAmount={handleChangeAmount}
            handleChangeDatePaid={handleChangeDatePaid}
            handleChangePaymentMethod={handleChangePaymentMethod}
            paymentMethod={paymentMethod}
            datePaid={datePaid}
            handleInitialPayment={handleInitialPayment}
            initialPayment={initialPayment}
            handleSecurityDeposit={handleSecurityDeposit}
            hasSecurityDeposit={hasSecurityDeposit}
            errors={errors}
          />
        }
        handleClose={handleCloseDialog}
        handleSubmit={handleSubmitTenantPayment}
        isSubmittingData={isSubmittingData}
      />
      <FormDialog
        open={openEditDialog}
        title="Edit Payment"
        text="Fill out the details below to add a new payment."
        content={
          <EditTenantPaymentForm
            paymentData={newPayment}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        }
        handleClose={handleCloseEditDialog}
        handleSubmit={handleSubmitEdittedTenantPayment}
        isSubmittingData={isSubmittingData}
      />
      <FormDialog
        open={openDeleteDialog}
        title="Delete Payment"
        content="Are you sure you want to delete this Payment?"
        handleClose={handleCloseDeleteDialog}
        handleSubmit={handleSubmitDeleteTenantPayment}
        isSubmittingData={isSubmittingData}
      />
      <FormDialog
        open={openVoidDialog}
        title="Void payment"
        content={
          paymentToVoid
            ? `Void payment of ${currencyFormatter(parseFloat(paymentToVoid.amount) || 0)} dated ${moment(
                paymentToVoid.date_paid
              ).format("LL")}? Only the latest successful payment for this tenant and unit can be voided.`
            : "Void this payment?"
        }
        handleClose={handleCloseVoidDialog}
        handleSubmit={handleSubmitVoidPayment}
        isSubmittingData={isVoidingPayment}
        buttonText="Void payment"
      />
      <TopSnack
        vertical={openSnack.vertical}
        horizontal={openSnack.horizontal}
        open={openSnack.open}
        handleCloseSnack={handleCloseSnack}
        message={message}
        severity={severity}
      />
    </Box>
  );
};

export default LandlordTenantsPayments;
